import React from 'react';
import { Card, CardContent, CardActions, Button, Typography, Stack, Tooltip, Box, Snackbar, Alert, CircularProgress, Skeleton } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import DuplicateCompareDialog from './DuplicateCompareDialog';
import DuplicateMergeDialog from './DuplicateMergeDialog';
import axiosInstance from '../../services/axiosInstance';
import { API_BASE_URL } from '../../config';

interface Props {
  cluster: any;
  onRefresh: () => void;
}

const DuplicateClusterCard: React.FC<Props> = ({ cluster, onRefresh }) => {
  const { type, strongHash, threshold, itemIds = [], entries = [] } = cluster;
  const [openCompare, setOpenCompare] = React.useState(false);
  const [openMerge, setOpenMerge] = React.useState(false);
  const [a, setA] = React.useState<any>(null);
  const [b, setB] = React.useState<any>(null);
  const [assets, setAssets] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [failedThumbnails, setFailedThumbnails] = React.useState<Set<string>>(new Set());
  const [snack, setSnack] = React.useState<{open: boolean; message: string; severity: 'success'|'info'|'warning'|'error'}>({open:false, message:'', severity:'success'});

  const handleWhitelistFirstPair = async () => {
    try {
      if (itemIds.length >= 2) {
        await axiosInstance.post('/api/duplicates/whitelist', { a: itemIds[0], b: itemIds[1] });
        onRefresh();
      }
    } catch {}
  };

  const openAB = async () => {
    try {
      if (itemIds.length >= 2) {
        const [ra, rb] = await Promise.all([
          axiosInstance.get(`/api/assets/${itemIds[0]}`),
          axiosInstance.get(`/api/assets/${itemIds[1]}`)
        ]);
        setA(ra.data); setB(rb.data); setOpenCompare(true);
      }
    } catch {}
  };

  React.useEffect(() => {
    // Ladda detaljer för att kunna visa thumbnails
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const toLoad = (itemIds || []).slice(0, 8);
        if (toLoad.length === 0) {
          setAssets([]);
          setLoading(false);
          return;
        }
        
        // Load each asset individually to handle partial failures
        const results = await Promise.allSettled(
          toLoad.map((id: string) => axiosInstance.get(`/api/assets/${id}`))
        );
        
        const loadedAssets: any[] = [];
        const failedIds: string[] = [];
        
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.data) {
            loadedAssets.push(result.value.data);
          } else {
            failedIds.push(toLoad[index]);
          }
        });
        
        setAssets(loadedAssets);
        
        if (failedIds.length > 0 && loadedAssets.length === 0) {
          setLoadError('Kunde inte ladda några bilder. De kan ha raderats.');
        } else if (failedIds.length > 0) {
          setSnack({ open: true, severity: 'warning', message: `${failedIds.length} bild(er) kunde inte laddas.` });
        }
      } catch (e: any) {
        console.error('Error loading duplicate assets:', e);
        setLoadError(e?.message || 'Kunde inte ladda bilder');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [JSON.stringify(itemIds)]);

  const thumbSrc = (asset: any) => {
    if (!asset) return '';
    if (asset.previewPaths?.thumbnail) return `${API_BASE_URL}${asset.previewPaths.thumbnail}`;
    if (asset.previewPaths?.medium) return `${API_BASE_URL}${asset.previewPaths.medium}`;
    return `${API_BASE_URL}/${(asset.filePath || '').replace(/\\/g, '/')}`;
  };

  const handleThumbnailError = (assetId: string) => {
    setFailedThumbnails(prev => new Set([...Array.from(prev), assetId]));
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const compareSelected = async () => {
    if (selected.length !== 2) return;
    try {
      const [ra, rb] = await Promise.all([
        axiosInstance.get(`/api/assets/${selected[0]}`),
        axiosInstance.get(`/api/assets/${selected[1]}`)
      ]);
      setA(ra.data); setB(rb.data); setOpenCompare(true);
    } catch {}
  };

  const mergeVersion = async () => {
    if (selected.length !== 2) return;
    try {
      await axiosInstance.post('/api/duplicates/merge-version', { primaryId: selected[0], secondaryId: selected[1] });
      onRefresh();
    } catch {}
  };

  return (
    <Card 
      sx={{ 
        mt: 2, 
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: 'none'
      }}
    >
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {type === 'exact' ? 'Exakt dublett' : 'Liknande bilder'} ({assets.length} bilder)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Klicka på bilder för att välja A och B, sedan använd knapparna nedan
        </Typography>
        
        {/* Loading state */}
        {loading && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" width={80} height={80} />
            ))}
          </Stack>
        )}
        
        {/* Error state */}
        {!loading && loadError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {loadError}
          </Alert>
        )}
        
        {/* No assets after loading */}
        {!loading && !loadError && assets.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Inga bilder att visa. Klustret kan vara föråldrat.
          </Alert>
        )}
        
        {/* Assets grid */}
        {!loading && assets.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {assets.map((as: any) => {
              const isSel = selected.includes(as._id);
              const idx = selected.indexOf(as._id);
              const hasThumbnailError = failedThumbnails.has(as._id);
              
              return (
                <Tooltip key={as._id} title={(as.title || as.fileName || as._id)}>
                  <Box 
                    onClick={() => toggleSelect(as._id)} 
                    sx={{ 
                      position: 'relative', 
                      cursor: 'pointer', 
                      borderRadius: '8px', 
                      border: isSel ? '3px solid #1976d2' : '1px solid #d4d4d4',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: isSel ? '#1976d2' : '#999'
                      }
                    }}
                  >
                    {hasThumbnailError ? (
                      /* Fallback for broken thumbnails */
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          padding: '4px'
                        }}
                      >
                        <BrokenImageIcon sx={{ color: '#999', fontSize: 32 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999', mt: 0.5, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                          {(as.fileName || 'Bild').slice(0, 10)}...
                        </Typography>
                      </Box>
                    ) : (
                      <Box 
                        component="img" 
                        src={thumbSrc(as)} 
                        alt={as.fileName}
                        onError={() => handleThumbnailError(as._id)}
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          objectFit: 'contain', 
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          padding: '4px'
                        }} 
                      />
                    )}
                    {isSel && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 4, 
                          left: 4, 
                          bgcolor: '#1976d2', 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: 20, 
                          height: 20, 
                          fontSize: '0.75rem', 
                          fontWeight: 'bold',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        {idx === 0 ? 'A' : 'B'}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        )}
      </CardContent>
      <CardActions>
        <Tooltip title="Laddar om listan över dubletter">
          <Button size="small" onClick={onRefresh}>Uppdatera</Button>
        </Tooltip>
        <Tooltip title="Markera valda två bilder som ej dubletter (whitelist)">
          <Button size="small" color="warning" onClick={async () => {
            try {
              if (selected.length === 2) {
                const resp = await axiosInstance.post('/api/duplicates/whitelist', { a: selected[0], b: selected[1] });
                setSnack({open:true, severity:'success', message: resp.data?.message || 'Markerat som ej dublett.'});
                const pair = [selected[0], selected[1]];
                setAssets(prev => prev.filter(as => !pair.includes(as._id)));
                setSelected([]);
                onRefresh();
              } else {
                const pair = itemIds.slice(0,2);
                const resp = await axiosInstance.post('/api/duplicates/whitelist', { a: pair[0], b: pair[1] });
                setSnack({open:true, severity:'success', message: resp.data?.message || 'Markerat som ej dublett.'});
                setAssets(prev => prev.filter(as => !pair.includes(as._id)));
                onRefresh();
              }
            } catch (e: any) {
              setSnack({open:true, severity:'error', message: e?.response?.data?.message || e?.message || 'Kunde inte markera som ej dublett.'});
            }
          }}>Ej dublett</Button>
        </Tooltip>
        <Tooltip title="Jämför valda två bilder (A och B)">
          <Button size="small" onClick={selected.length === 2 ? compareSelected : openAB}>Jämför A/B</Button>
        </Tooltip>
        <Tooltip title="Slå ihop: öppna dialog för val av metadata och lägg B som version på A">
          <Button size="small" onClick={() => {
            if (selected.length !== 2) { setSnack({open:true, severity:'info', message:'Välj två bilder (A och B).'}); return; }
            setOpenMerge(true);
          }} disabled={selected.length !== 2}>Merge (B → A version)</Button>
        </Tooltip>
        <Tooltip title="Arkivera vald sekundär (B) – kan återställas senare">
          <Button size="small" onClick={async () => {
            try {
              if (selected.length !== 2) { setSnack({open:true, severity:'info', message:'Välj två bilder (A och B).'}); return; }
              const resp = await axiosInstance.post('/api/duplicates/archive', { assetId: selected[1] });
              setSnack({open:true, severity:'success', message: resp.data?.message || 'Arkiverad.'});
              setAssets(prev => prev.filter(as => as._id !== selected[1]));
              setSelected([]);
              onRefresh();
            } catch (e: any) {
              setSnack({open:true, severity:'error', message: e?.response?.data?.message || e?.message || 'Arkivering misslyckades.'});
            }
          }} disabled={selected.length !== 2}>Arkivera B</Button>
        </Tooltip>
      </CardActions>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({...s, open:false}))} anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
      {openCompare && a && b && (
        <DuplicateCompareDialog open={openCompare} assetA={a} assetB={b} onClose={() => setOpenCompare(false)} />
      )}
      {openMerge && selected.length === 2 && (
        <DuplicateMergeDialog
          open={openMerge}
          assetA={assets.find(as => as._id === selected[0])}
          assetB={assets.find(as => as._id === selected[1])}
          onClose={() => setOpenMerge(false)}
          onMerged={() => {
            const pair = [selected[0], selected[1]];
            setAssets(prev => prev.filter(as => !pair.includes(as._id)));
            setSelected([]); onRefresh();
            setSnack({open:true, severity:'success', message:'Merge klar.'});
          }}
        />
      )}
    </Card>
  );
};

export default DuplicateClusterCard;


